import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@produzionejb/ui';
import type { InvCreateRequest, InvFinishedSkuLine, InvLotLine, InvPackagingLine, InvFinishedBoxLine } from '@produzionejb/types';
import { InvFinishedBoxSection, type FinishedBoxLineDraft } from './InvFinishedBoxSection';
import { ApiError } from '../../lib/api-client';
import {
  useCreateInv,
  useInv,
  useUpdateInv,
  useCloseInv,
  useDeleteInv,
} from '../../lib/inv-api';
import { LotPicker } from '../../components/LotPicker';
import { PackagingItemPicker } from '../../components/PackagingItemPicker';
import { ProductPicker } from '../../components/ProductPicker';
import { BackToStockMovementsLink } from '../../components/BackToStockMovementsLink';

type LotRow = {
  key: string;
  lotId: string;
  lotCode?: string;
  position: string;
  qtyCounted: string;
  notes: string;
};

type PackagingRow = {
  key: string;
  packagingItemId: string;
  packagingItemName?: string;
  packagingItemCategory?: string;
  qtyCounted: string;
  notes: string;
};

type FinishedRow = {
  key: string;
  productId: string;
  productCode?: string;
  productName?: string;
  qty5g: string;
  qty10g: string;
  qty20g: string;
  qty50g: string;
  qty100g: string;
  notes: string;
};

function newKey() {
  return Math.random().toString(36).slice(2, 9);
}

function todayIso() {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

export function InvFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const invQ = useInv(id);
  const createMut = useCreateInv();
  const updateMut = useUpdateInv(id ?? '');
  const closeMut = useCloseInv(id ?? '');
  const deleteMut = useDeleteInv();

  const [draftName, setDraftName] = useState('');
  const [workDate, setWorkDate] = useState(todayIso());
  const [notes, setNotes] = useState('');
  const [lotRows, setLotRows] = useState<LotRow[]>([]);
  const [packagingRows, setPackagingRows] = useState<PackagingRow[]>([]);
  const [finishedRows, setFinishedRows] = useState<FinishedRow[]>([]);
  const [finishedBoxRows, setFinishedBoxRows] = useState<FinishedBoxLineDraft[]>([]);
  const [showOnlyDelta, setShowOnlyDelta] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const readOnly = isEdit && (invQ.data?.status !== 'DRAFT' || invQ.data?.locked);

  useEffect(() => {
    if (isEdit && invQ.data) {
      const i = invQ.data;
      setDraftName(i.draftName ?? '');
      setWorkDate(i.workDate.slice(0, 10));
      setNotes(i.notes ?? '');
      setLotRows(
        i.lotLines.map((l) => ({
          key: l.id,
          lotId: l.lotId,
          lotCode: l.lotCode ?? undefined,
          position: l.position ?? '',
          qtyCounted: l.qtyCounted,
          notes: l.notes ?? '',
        })),
      );
      setPackagingRows(
        i.packagingLines.map((l) => ({
          key: l.id,
          packagingItemId: l.packagingItemId,
          packagingItemName: l.packagingItemName,
          packagingItemCategory: l.packagingItemCategory,
          qtyCounted: String(l.qtyCounted),
          notes: l.notes ?? '',
        })),
      );
      setFinishedRows(
        i.finishedLines.map((l) => ({
          key: l.id,
          productId: l.productId,
          productCode: l.productCode,
          productName: l.productName,
          qty5g: String(l.qty5g),
          qty10g: String(l.qty10g),
          qty20g: String(l.qty20g),
          qty50g: String(l.qty50g),
          qty100g: String(l.qty100g),
          notes: l.notes ?? '',
        })),
      );
      setFinishedBoxRows(
        i.finishedBoxLines.map((l) => ({
          key: l.id,
          productSkuId: l.productSkuId,
          productSkuLabel: `${l.productSkuCode} (${l.grammatura}g)`,
          lotPositionId: l.lotPositionId ?? null,
          lotPositionLabel: l.lotCode ? `${l.lotCode}` : null,
          qtyBoxesCounted: String(l.qtyBoxesCounted),
          qtyBoxesSystem: l.qtyBoxesSystem,
          delta: l.delta,
          notes: l.notes ?? '',
        })),
      );
    }
  }, [isEdit, invQ.data]);

  // CRUD righe
  function addLotRow() {
    setLotRows((rows) => [
      ...rows,
      { key: newKey(), lotId: '', position: '', qtyCounted: '', notes: '' },
    ]);
  }
  function removeLotRow(key: string) {
    setLotRows((rows) => rows.filter((r) => r.key !== key));
  }
  function updateLotRow(key: string, patch: Partial<LotRow>) {
    setLotRows((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }
  function addPackagingRow() {
    setPackagingRows((rows) => [
      ...rows,
      { key: newKey(), packagingItemId: '', qtyCounted: '', notes: '' },
    ]);
  }
  function removePackagingRow(key: string) {
    setPackagingRows((rows) => rows.filter((r) => r.key !== key));
  }
  function updatePackagingRow(key: string, patch: Partial<PackagingRow>) {
    setPackagingRows((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }
  function addFinishedRow() {
    setFinishedRows((rows) => [
      ...rows,
      {
        key: newKey(),
        productId: '',
        qty5g: '0',
        qty10g: '0',
        qty20g: '0',
        qty50g: '0',
        qty100g: '0',
        notes: '',
      },
    ]);
  }
  function removeFinishedRow(key: string) {
    setFinishedRows((rows) => rows.filter((r) => r.key !== key));
  }
  function updateFinishedRow(key: string, patch: Partial<FinishedRow>) {
    setFinishedRows((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function buildPayload(): InvCreateRequest {
    return {
      draftName: draftName.trim() || null,
      workDate: new Date(workDate),
      notes: notes.trim() || null,
      lotLines: lotRows
        .filter((r) => r.lotId && r.qtyCounted !== '')
        .map<InvLotLine>((r) => ({
          lotId: r.lotId,
          position: r.position.trim() || null,
          qtyCounted: Number(r.qtyCounted),
          notes: r.notes.trim() || null,
        })),
      packagingLines: packagingRows
        .filter((r) => r.packagingItemId && r.qtyCounted !== '')
        .map<InvPackagingLine>((r) => ({
          packagingItemId: r.packagingItemId,
          qtyCounted: Number(r.qtyCounted),
          notes: r.notes.trim() || null,
        })),
      finishedLines: finishedRows
        .filter((r) => r.productId)
        .map<InvFinishedSkuLine>((r) => ({
          productId: r.productId,
          qty5g: Number(r.qty5g) || 0,
          qty10g: Number(r.qty10g) || 0,
          qty20g: Number(r.qty20g) || 0,
          qty50g: Number(r.qty50g) || 0,
          qty100g: Number(r.qty100g) || 0,
          notes: r.notes.trim() || null,
        })),
      finishedBoxLines: finishedBoxRows
        .filter((r) => r.productSkuId && r.qtyBoxesCounted !== '')
        .map<InvFinishedBoxLine>((r) => ({
          productSkuId: r.productSkuId,
          lotPositionId: r.lotPositionId,
          qtyBoxesCounted: Number(r.qtyBoxesCounted) || 0,
          notes: r.notes.trim() || null,
        })),
    };
  }

  async function handleSave() {
    setErrorMsg(null);
    try {
      if (isEdit && id) {
        await updateMut.mutateAsync(buildPayload());
      } else {
        const created = await createMut.mutateAsync(buildPayload());
        navigate(`/documents/inv/${created.id}/edit`);
      }
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message;
      setErrorMsg(msg);
    }
  }

  async function handleClose() {
    if (!id) return;
    if (lotRows.filter((r) => r.lotId && r.qtyCounted !== '').length === 0) {
      setErrorMsg(t('inv.form.errorEmptyLots'));
      return;
    }
    if (!window.confirm(t('inv.form.confirmClose'))) return;
    setErrorMsg(null);
    try {
      await updateMut.mutateAsync(buildPayload());
      await closeMut.mutateAsync();
      navigate('/documents/inv');
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message;
      setErrorMsg(msg);
    }
  }

  async function handleDelete() {
    if (!id) return;
    const label = invQ.data?.code ? `INV ${invQ.data.code}` : (draftName || 'INV');
    if (!window.confirm(t('inv.list.confirmDelete', { name: label }))) return;
    try {
      await deleteMut.mutateAsync(id);
      navigate('/documents/inv');
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message;
      setErrorMsg(msg);
    }
  }

  const reconciliation = invQ.data?.reconciliation;
  const reconciliationFiltered = useMemo(() => {
    if (!reconciliation) return [];
    if (showOnlyDelta) {
      return reconciliation.items.filter((r) => Number(r.differenza) !== 0);
    }
    return reconciliation.items;
  }, [reconciliation, showOnlyDelta]);

  const isSubmitting = createMut.isPending || updateMut.isPending || closeMut.isPending;

  if (isEdit && invQ.isLoading) {
    return <p className="text-sm text-slate-500">{t('common.loading')}</p>;
  }
  if (isEdit && invQ.error) {
    return (
      <p className="text-sm text-red-600">
        {t('inv.form.errorLoad')}: {(invQ.error as Error).message}
      </p>
    );
  }

  const titleLabel = isEdit
    ? invQ.data?.code
      ? `INV ${invQ.data.code}`
      : (invQ.data?.draftName ?? t('inv.form.titleEdit'))
    : t('inv.form.titleNew');

  return (
    <div className="space-y-4">
      <BackToStockMovementsLink />
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">{titleLabel}</h2>
        <div className="flex items-center gap-2">
          {isEdit && invQ.data && (
            <>
              <span
                className={
                  invQ.data.status === 'DRAFT'
                    ? 'rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700'
                    : 'rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700'
                }
              >
                {invQ.data.status === 'DRAFT' ? t('inv.list.statusDraft') : t('inv.list.statusClosed')}
              </span>
              {invQ.data.locked && <span title={t('inv.form.lockedTip')}>🔒</span>}
            </>
          )}
          <Button variant="secondary" onClick={() => navigate('/documents/inv')}>
            {t('common.back')}
          </Button>
        </div>
      </div>

      {errorMsg && (
        <Card>
          <CardContent>
            <p className="text-sm text-red-600">{errorMsg}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('inv.sections.header')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="draftName">{t('inv.fields.draftName')}</Label>
            <Input
              id="draftName"
              placeholder={t('inv.form.draftNamePlaceholder')}
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              disabled={readOnly}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="workDate">{t('inv.fields.workDate')} *</Label>
            <Input
              id="workDate"
              type="date"
              value={workDate}
              onChange={(e) => setWorkDate(e.target.value)}
              disabled={readOnly}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-3">
            <Label htmlFor="notes">{t('inv.fields.notes')}</Label>
            <textarea
              id="notes"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={readOnly}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('inv.sections.lotLines')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {lotRows.length === 0 && (
            <p className="text-sm text-slate-500">{t('inv.lotLines.empty')}</p>
          )}
          {lotRows.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-2 py-2">{t('inv.fields.lotto')}</th>
                  <th className="px-2 py-2">{t('inv.fields.position')}</th>
                  <th className="px-2 py-2 text-right">{t('inv.fields.qtyCounted')}</th>
                  <th className="px-2 py-2">{t('inv.fields.note')}</th>
                  <th className="px-2 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {lotRows.map((row) => (
                  <tr key={row.key} className="border-b border-slate-100">
                    <td className="px-2 py-1">
                      <LotPicker
                        value={row.lotId}
                        valueCode={row.lotCode}
                        onChange={(id2, code) => updateLotRow(row.key, { lotId: id2, lotCode: code })}
                        disabled={readOnly}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        className="w-32 rounded border border-slate-300 px-2 py-1 text-xs"
                        placeholder="scaffale A..."
                        value={row.position}
                        onChange={(e) => updateLotRow(row.key, { position: e.target.value })}
                        disabled={readOnly}
                      />
                    </td>
                    <td className="px-2 py-1 text-right">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="w-24 rounded border border-slate-300 px-2 py-1 text-right text-xs"
                        value={row.qtyCounted}
                        onChange={(e) => updateLotRow(row.key, { qtyCounted: e.target.value })}
                        disabled={readOnly}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                        value={row.notes}
                        onChange={(e) => updateLotRow(row.key, { notes: e.target.value })}
                        disabled={readOnly}
                      />
                    </td>
                    <td className="px-2 py-1 text-right">
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => removeLotRow(row.key)}
                          className="text-red-600 hover:underline text-xs"
                        >
                          ✕
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!readOnly && (
            <Button variant="secondary" size="sm" onClick={addLotRow}>
              + {t('inv.lotLines.addRow')}
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              {t('inv.sections.reconciliation')}
              {reconciliation && (
                <span className="ml-2 text-sm font-normal text-slate-500">
                  ({reconciliation.countWithDelta} {t('inv.reconciliation.withDelta')} / {reconciliation.countOk} OK)
                </span>
              )}
            </CardTitle>
            {reconciliation && (
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={showOnlyDelta}
                  onChange={(e) => setShowOnlyDelta(e.target.checked)}
                />
                {t('inv.reconciliation.onlyDelta')}
              </label>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {!reconciliation && (
            <p className="p-4 text-sm text-slate-500">{t('inv.reconciliation.empty')}</p>
          )}
          {reconciliation && reconciliationFiltered.length === 0 && (
            <p className="p-4 text-sm text-emerald-700">{t('inv.reconciliation.allOk')}</p>
          )}
          {reconciliation && reconciliationFiltered.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-3 py-2">{t('inv.fields.lotto')}</th>
                  <th className="px-3 py-2">{t('inv.fields.prodotto')}</th>
                  <th className="px-3 py-2 text-right">{t('inv.reconciliation.qtyInv')}</th>
                  <th className="px-3 py-2 text-right">{t('inv.reconciliation.qtyCdc')}</th>
                  <th className="px-3 py-2 text-right">{t('inv.reconciliation.delta')}</th>
                  <th className="px-3 py-2">{t('inv.reconciliation.flag')}</th>
                </tr>
              </thead>
              <tbody>
                {reconciliationFiltered.map((r) => {
                  const delta = Number(r.differenza);
                  const bgClass = delta === 0
                    ? ''
                    : Math.abs(delta) / Math.max(Number(r.qtyFromCdc), 1) > 0.05
                      ? 'bg-red-50'
                      : 'bg-amber-50';
                  return (
                    <tr key={r.lotId} className={`border-b border-slate-100 ${bgClass}`}>
                      <td className="px-3 py-1 font-mono text-xs">{r.lotCode}</td>
                      <td className="px-3 py-1 text-xs">
                        {r.productCode ? `${r.productCode} — ` : ''}{r.productName ?? ''}
                      </td>
                      <td className="px-3 py-1 text-right font-mono text-xs">{r.qtyCountedSum}g</td>
                      <td className="px-3 py-1 text-right font-mono text-xs">{r.qtyFromCdc}g</td>
                      <td className={`px-3 py-1 text-right font-mono text-xs font-semibold ${delta > 0 ? 'text-blue-700' : delta < 0 ? 'text-red-700' : 'text-slate-700'}`}>
                        {delta > 0 ? '+' : ''}{r.differenza}g
                      </td>
                      <td className="px-3 py-1 text-xs">
                        {r.notInInventory && (
                          <span className="rounded bg-orange-100 px-2 py-0.5 text-orange-700">
                            {t('inv.reconciliation.notInInventory')}
                          </span>
                        )}
                        {r.notInCdc && (
                          <span className="rounded bg-purple-100 px-2 py-0.5 text-purple-700">
                            {t('inv.reconciliation.notInCdc')}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('inv.sections.packagingLines')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {packagingRows.length === 0 && (
            <p className="text-sm text-slate-500">{t('inv.packagingLines.empty')}</p>
          )}
          {packagingRows.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-2 py-2">{t('inv.fields.packagingItem')}</th>
                  <th className="px-2 py-2 text-right">{t('inv.fields.qtyCounted')}</th>
                  <th className="px-2 py-2">{t('inv.fields.note')}</th>
                  <th className="px-2 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {packagingRows.map((row) => (
                  <tr key={row.key} className="border-b border-slate-100">
                    <td className="px-2 py-1">
                      <PackagingItemPicker
                        value={row.packagingItemId}
                        valueName={row.packagingItemName}
                        valueCategory={row.packagingItemCategory}
                        onChange={(id2, name, category) =>
                          updatePackagingRow(row.key, {
                            packagingItemId: id2,
                            packagingItemName: name,
                            packagingItemCategory: category,
                          })
                        }
                        disabled={readOnly}
                      />
                    </td>
                    <td className="px-2 py-1 text-right">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className="w-24 rounded border border-slate-300 px-2 py-1 text-right text-xs"
                        value={row.qtyCounted}
                        onChange={(e) => updatePackagingRow(row.key, { qtyCounted: e.target.value })}
                        disabled={readOnly}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                        value={row.notes}
                        onChange={(e) => updatePackagingRow(row.key, { notes: e.target.value })}
                        disabled={readOnly}
                      />
                    </td>
                    <td className="px-2 py-1 text-right">
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => removePackagingRow(row.key)}
                          className="text-red-600 hover:underline text-xs"
                        >
                          ✕
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!readOnly && (
            <Button variant="secondary" size="sm" onClick={addPackagingRow}>
              + {t('inv.packagingLines.addRow')}
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('inv.sections.finishedLines')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {finishedRows.length === 0 && (
            <p className="text-sm text-slate-500">{t('inv.finishedLines.empty')}</p>
          )}
          {finishedRows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-600">
                  <tr>
                    <th className="px-2 py-2">{t('inv.fields.prodotto')}</th>
                    <th className="px-2 py-2 text-right">5g</th>
                    <th className="px-2 py-2 text-right">10g</th>
                    <th className="px-2 py-2 text-right">20g</th>
                    <th className="px-2 py-2 text-right">50g</th>
                    <th className="px-2 py-2 text-right">100g</th>
                    <th className="px-2 py-2">{t('inv.fields.note')}</th>
                    <th className="px-2 py-2 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {finishedRows.map((row) => (
                    <tr key={row.key} className="border-b border-slate-100">
                      <td className="px-2 py-1">
                        <ProductPicker
                          value={row.productId}
                          valueCode={row.productCode}
                          valueName={row.productName}
                          onChange={(id2, code, name) =>
                            updateFinishedRow(row.key, {
                              productId: id2,
                              productCode: code,
                              productName: name,
                            })
                          }
                          disabled={readOnly}
                        />
                      </td>
                      {(['qty5g', 'qty10g', 'qty20g', 'qty50g', 'qty100g'] as const).map((field) => (
                        <td key={field} className="px-2 py-1 text-right">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            className="w-14 rounded border border-slate-300 px-1 py-1 text-right text-xs"
                            value={row[field]}
                            onChange={(e) => updateFinishedRow(row.key, { [field]: e.target.value })}
                            disabled={readOnly}
                          />
                        </td>
                      ))}
                      <td className="px-2 py-1">
                        <input
                          type="text"
                          className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                          value={row.notes}
                          onChange={(e) => updateFinishedRow(row.key, { notes: e.target.value })}
                          disabled={readOnly}
                        />
                      </td>
                      <td className="px-2 py-1 text-right">
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={() => removeFinishedRow(row.key)}
                            className="text-red-600 hover:underline text-xs"
                          >
                            ✕
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!readOnly && (
            <Button variant="secondary" size="sm" onClick={addFinishedRow}>
              + {t('inv.finishedLines.addRow')}
            </Button>
          )}
        </CardContent>
      </Card>

      <InvFinishedBoxSection
        value={finishedBoxRows}
        onChange={setFinishedBoxRows}
        readOnly={readOnly}
      />

      {!readOnly && (
        <div className="flex justify-between gap-2">
          <div>
            {isEdit && (
              <Button
                variant="secondary"
                onClick={handleDelete}
                disabled={isSubmitting}
                className="!bg-red-50 !text-red-700 !border-red-200 hover:!bg-red-100"
              >
                {t('common.delete')}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={handleSave} disabled={isSubmitting}>
              {t('inv.form.saveButton')}
            </Button>
            {isEdit && (
              <Button onClick={handleClose} disabled={isSubmitting}>
                {t('inv.form.closeButton')}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
